import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {EnvironmentService} from '@core/environment/environment.service';
import {LoggedUserDataModel} from './response/logged-user-data.model';
import {Observable} from 'rxjs';
import {UserDataModel} from '@core/modules/rest/user/response/user-data.model';
import {CreateUserRequestModel} from '@core/modules/rest/user/request/create-user-request.model';
import {CreateUserResponseModel} from '@core/modules/rest/user/response/create-user-response.model';
import {SaveUserSettingsRequestModel} from '@core/modules/rest/user/request/save-user-settings-request.model';
import {SaveUserPersonalSettingsRequestModel} from '@core/modules/rest/user/request/save-user-personal-settings-request.model';
import {AlterUserActiveStatusRequestModel} from '@core/modules/rest/user/request/alter-user-active-status-request.model';
import {SaveUserRolesRequestModel} from '../../../../views/secured/views/modules/user-management/components/popups/edit-roles-popup/model/save-user-roles-request.model';
import {UserSettingsModel} from '@core/modules/rest/user/response/user-settings.model';
import {AllUserSettingsRequestModel} from '@core/modules/rest/user/request/all-user-settings-request.model';
import {TermsAndConditionsResponseModel} from '@core/modules/rest/user/response/terms-and-conditions-response.model';
import {Router} from '@angular/router';
import {SaveEducationSettingsRequestModel} from '@core/modules/rest/user/request/save-education-settings-request.model';
import {SearchDataModel} from '@shared/model/search-data.model';
import {TextSizeEnum} from '../../../../views/secured/views/modules/display-consent/enums/text-size.enum';

@Injectable()
export class UserRestService {

    private controllerUrl = this.environmentService.serverApiUrl + '/secured/users';

    constructor(private httpClient: HttpClient, private router: Router, private environmentService: EnvironmentService) {
    }

    getLoggedUser(): Observable<LoggedUserDataModel> {
        const url = this.controllerUrl.concat('/logged');
        return this.httpClient.get<LoggedUserDataModel>(url);
    }

    getUsersByOrganization(organization: string, offset: number): Observable<UserDataModel[]> {
        const url = this.controllerUrl.concat('/organization/').concat(organization).concat(`/offset/${offset}`);
        return this.httpClient.get<UserDataModel[]>(url);
    }

    getFilteredUsersByOrganization(organization: string, searchData: SearchDataModel): Observable<UserDataModel[]> {
        const url = this.controllerUrl.concat('/organization/').concat(organization).concat('/filter');
        return this.httpClient.post<UserDataModel[]>(url, searchData);
    }

    createUser(createUserRequest: CreateUserRequestModel): Observable<CreateUserResponseModel> {
        const url = this.controllerUrl.concat('/create');
        return this.httpClient.post<CreateUserResponseModel>(url, createUserRequest);
    }

    user(userId: string): Observable<UserDataModel> {
        const url = this.controllerUrl.concat('/').concat(userId);
        return this.httpClient.get<UserDataModel>(url);
    }

    saveSettings(request: AllUserSettingsRequestModel): Observable<void> {
        const userId = request.userId;
        const url = this.controllerUrl.concat(`/${userId}/updateSettings`);
        return this.httpClient.put<void>(url, request);
    }

    saveProfileSettings(saveUserPersonalSettingsRequest: SaveUserPersonalSettingsRequestModel): void {
        const url = this.controllerUrl.concat('/').concat(saveUserPersonalSettingsRequest.userId).concat('/updateProfile');
        this.httpClient.post<UserDataModel>(url, saveUserPersonalSettingsRequest).subscribe();
    }

    updatePersonalSettings(settings: string): Observable<void> {
        return this.httpClient.put<void>(this.controllerUrl.concat('/updatePersonalSettings'), settings);
    }

    saveRoles(saveUserRolesRequest: SaveUserRolesRequestModel): Observable<void> {
        const url = this.controllerUrl.concat('/').concat(saveUserRolesRequest.userId).concat('/saveRoles');
        return this.httpClient.post<void>(url, saveUserRolesRequest);
    }

    alterUserActiveStatus(request: AlterUserActiveStatusRequestModel): Observable<UserDataModel> {
        const url = this.controllerUrl.concat('/').concat(request.userId).concat('/alterStatus');
        return this.httpClient.post<UserDataModel>(url, request);
    }

    deleteUser(userId: string): Observable<void> {
        const url = this.controllerUrl.concat('/').concat(userId);
        return this.httpClient.delete<void>(url);
    }

    updatePersonalSettingsForSpecificUser(userId: string, settings: string): void {
        this.httpClient.put(this.controllerUrl.concat(`/${userId}/updatePersonalSettings`), settings).subscribe();
    }

    getOrganizationDefaultSettings(): Observable<UserSettingsModel> {
        return this.httpClient.get<UserSettingsModel>(this.controllerUrl.concat('/organization-default-settings'));
    }

    updateUserSettingsOnCreation(settings: SaveUserSettingsRequestModel): Observable<void> {
        const userId = settings.userId;
        return this.httpClient.put<void>(this.controllerUrl.concat(`/${userId}/settings`), settings);
    }

    acceptTermsAndConditions(): Observable<void> {
        return this.httpClient.post<void>(this.controllerUrl.concat('/accept-terms-and-conditions'), null);
    }

    checkTermsAndConditions(): Observable<TermsAndConditionsResponseModel> {
        return this.httpClient.get<TermsAndConditionsResponseModel>(this.controllerUrl.concat('/terms-and-conditions'));
    }

    saveEducationSettings(request: SaveEducationSettingsRequestModel): Observable<UserDataModel> {
        return this.httpClient.post<UserDataModel>(this.controllerUrl.concat(`/${request.userId}/education-settings`), request);
    }

    updateTextSize(textSize: TextSizeEnum): void {
        this.httpClient.put<void>(this.controllerUrl.concat('/updateTextSize'), textSize).subscribe();
    }

    reinviteUser(userId: string): Observable<void> {
        return this.httpClient.get<void>(this.controllerUrl.concat(`/${userId}/reinvite`));
    }

    getConsentingActive(userId: string): Observable<boolean> {
        return this.httpClient.get<boolean>(this.controllerUrl + '/consenting-active'.concat(`/${userId}`));
    }
}
